<template>
  <ValidationObserver
    v-if="customFieldList[organisationId]"
    ref="contactFieldObserver"
    v-slot="{ valid, pristine }" >
    <b-modal
      id="contact-field-modal"
      ref="contactFieldModal"
      modal-class="settings-modal"
      centered
      :scrollable="isMoreThan3Options"
      hide-header-close
      :title="modalTitle"
      size="sm"
      @hidden="resetFields"
      @shown="focus" >
      <div>
        <ValidationProvider
          v-slot="{ errors, dirty }"
          name="name"
          :rules="{
            required: true,
            noSpecialCharacters: {
              message: 'Name can\'t contain special characters.',
            },
            noDuplicate: {
              list: customFieldList[organisationId].map(field => field.name).filter(field => field !== initialEditName),
              caseSensitive: false,
              message: 'Contact field already exists.'
            }
          }"
          mode="aggressive" >
          <b-form-group>
            <label
              for="p-key"
              class="settings__label mt-0" >
              Name
            </label>
            <input
              ref="nameInputRef"
              v-model="fieldName"
              type="text"
              class="w-100 sb2-bordered sb2-hoverable sb2-focusable"
              placeholder="Enter contact field name" >
            <div
              v-if="errors[0] && dirty"
              class="error flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.ERROR"
                :color="SBColors.redPrimary"
                :size="12" />
              <span>{{ errors[0] }}</span>
            </div>
          </b-form-group>
        </ValidationProvider>
      </div>

      <ValidationProvider
        v-slot="{ errors, dirty }"
        name="type"
        rules="required"
        mode="eager"
        tag="div" >
        <b-form-group>
          <label
            for="p-key"
            class="settings__label" >
            Type
          </label>
          <div id="type-input">
            <multiselect
              v-model="type"
              :disabled="update"
              placeholder="Select a type"
              class="flex-options hide-tags no-selected-background"
              :options="fieldTypes"
              label="name"
              track-by="alias"
              :multiple="false"
              :show-labels="false"
              :show-no-results="false"
              :close-on-select="true"
              :clear-on-select="false"
              :preserve-search="true"
              :allow-empty="false"
              :searchable="false" >
              <template
                slot="option"
                slot-scope="props">
                <SBIcon
                  :icon="SBIconList.DONE_TICK"
                  :color="SBColors.bluePrimary"
                  :size="9"/>
                <span class="ellipsis">
                  {{ props.option.name }}
                </span>
              </template>
            </multiselect>
          </div>
          <b-popover
            v-if="update"
            target="type-input"
            triggers="hover"
            placement="bottom"
            custom-class="basic-popover">
            This can't be edited.
          </b-popover>
          <div
            v-if="errors[0] && dirty"
            class="error flex flex-align-center">
            <SBIcon
              class="mr-2"
              :icon="SBIconList.ERROR"
              :color="SBColors.redPrimary"
              :size="12" />
            <span>{{ errors[0] }}</span>
          </div>
        </b-form-group>
      </ValidationProvider>

      <!-- OPTIONS values -->
      <div v-if="type.alias === 'list'">
        <b-form-group>
          <label
            for="p-key"
            class="settings__label" >
            Options
          </label>
          
          <div
            v-for="(option, id) in options"
            :key="id"
            class="option">
            <ValidationProvider
              v-slot="{ errors, dirty }"
              :name="`option-${id}`"
              :rules="{
                noDuplicate: {
                  list: options.map(field => field.name).filter((field, index) => index !== id),
                  caseSensitive: false,
                  message: 'Duplicate option'
                },
                required: true,
              }"
              mode="aggressive"
              tag="div" >
              <input
                :ref="`optionInputRef-${id}`"
                v-model="option.name"
                type="text"
                class="w-100 sb2-bordered sb2-hoverable sb2-focusable option-item"
                placeholder="Option name" >
              <SBIcon
                class="mr-2 delete"
                :icon="SBIconList.TRASH"
                :size="12"
                :hover-size="22"
                :color="SBColors.blackUltralight"
                :background-color="SBColors.white"
                :hover-icon-color="SBColors.blackPrimary"
                :hover-color="SBColors.greyBorderSecondary"
                @click="removeOption(id)" />
  
              <div
                v-if="errors[0] && dirty"
                class="error flex flex-align-center">
                <SBIcon
                  class="mr-2"
                  :icon="SBIconList.ERROR"
                  :color="SBColors.redPrimary"
                  :size="12" />
                <span>{{ errors[0] }}</span>
              </div>

            </ValidationProvider>
          </div>

          <div
            class="add-option-btn"
            @click="addOption">
            <span class="flex flex-align-center">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.bluePrimary"
                :size="8" />
              <span>Add option</span>
            </span>
          </div>
        </b-form-group>
      </div>

      <template v-if="validationErrors">
        <div
          v-for="(error, index) in validationErrorsList"
          :key="index"
          class="error-message fs-14 mt-3 flex flex-align-center px-0" >
          <SBIcon
            class="mr-2"
            :icon="SBIconList.ERROR"
            :color="SBColors.redPrimary"
            :size="12" />
          {{ error }}
        </div>
      </template>

      <template slot="modal-footer">
        <b-button
          :disabled="saving"
          @click="hide">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          :disabled="saving || !valid || (pristine && !listEdited)"
          :class="{loading: saving}"
          class="flex settings__actions--save"
          @click="commitUpdate">
          <span>Save</span>
          <Spinner
            v-if="saving"
            :size="14"
            :thickness="2"
            :color="SBColors.white" />
        </b-button>
      </template>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

import ToasterMixin from '@/mixins/Toaster';
import SBIconList from '@/helpers/lists/sb-icon-list';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import Spinner from '@/components/common/Spinner';
import setValidationErrors from '@/helpers/validationErrors';
import fieldTypes from '@/helpers/lists/fieldTypes';

export default {
  name: 'CreateContactFieldModal',
  components: {
    Multiselect,
    SBIcon,
    Spinner,
  },
  mixins: [
    ToasterMixin,
  ],
  props: {},
  data() {
    return {
      saving: false,
      validationErrors: null,
      update: false,
      initialEditName: '',
      initialOptionsList: [{ name: '' }],
      fieldName: '',
      contactFieldId: null,
      type: {},
      options: [{ name: '' }],
      modalTitle: null,
      SBIconList,
      SBColors,
      fieldTypes,
    };
  },
  computed: {
    ...mapGetters({
      customFieldList: 'contact/customFieldList',
      apiError: 'contact/apiError',
    }),
    organisationId() {
      return this.$route.params.organisationId;
    },
    validationErrorsList() {
      return Object.values(this.validationErrors);
    },
    isMoreThan3Options() {
      return this.options.length > 3;
    },
    listEdited() {
      return JSON.stringify(this.initialOptionsList.map(option => option.name)) !== JSON.stringify(this.options.map(option => option.name));
    },
  },
  watch: {
    apiError(error) {
      this.saving = false;
      if (error.status === 422) {
        this.validationErrors = setValidationErrors(error.data.errors);
      } else {
        this.errorMessage = error.data.message;
      }
    },
  },
  methods: {
    ...mapActions({
      createCustomField: 'contact/createCustomField',
      updateCustomField: 'contact/updateCustomField',
      fetchCustomFields: 'contact/fetchCustomFields20',
      updateContactFieldsList: 'contact/updateContactFieldsList',
    }),
    addOption() {
      this.options.push({ name: '' });
      const targetOption = `optionInputRef-${this.options.length - 1}`;
      setTimeout(() => {
        this.$refs[targetOption][0].focus();
      }, 0);
    },
    removeOption(index) {
      this.options.splice(index, 1);
    },
    resetFields() {
      this.update = false;
      this.fieldName = '';
      this.contactFieldId = null;
      this.saving = false;
      this.validationErrors = null;
      this.options = [{ name: '' }];
      this.saving = false;
    },
    commitUpdate() {
      this.$refs.contactFieldObserver.validate()
        .then((valid) => {
          if (valid) {
            this.saving = true;
            this.validationErrors = null;
            const data = {
              name: this.fieldName,
              type: this.type.alias,
              options: this.type.alias === 'list' ? this.options : null,
            };

            if (this.update) {
              this.updateCustomField({
                contactFieldId: this.contactFieldId,
                data,
              }).then((response) => {
                if (response.status === 200) {
                  this.initialEditName = this.fieldName;
                  // this.$emit('custom-field-added', response.data.field);
                  // refactor update
                  // this.fetchCustomFields({
                  //   silent: true,
                  //   organisationId: this.organisationId,
                  // })
                  //   .then((response) => {
                  //     if (response.status === 200) {
                  //       
                  //     }
                  //   });
                  this.showToaster('Contact field has been successfully updated.', null, null, 'OK');
                  this.hide();
                }
              });
            } else {
              this.createCustomField({
                data,
              }).then((response) => {
                if (response.status === 200) {
                  this.initialEditName = this.fieldName;
                  this.showToaster('Contact field has been successfully created.', null, null, 'OK');
                  this.hide();
                }
              });
            }
          }
        });
    },
    show(contactField = null) {
      this.modalTitle = 'New contact field';
      this.type = this.fieldTypes[0];
      if (contactField) {
        this.modalTitle = `Edit ${contactField.name}`;
        this.update = true;
        this.initialEditName = contactField.name;
        this.fieldName = contactField.name;
        this.type = this.fieldTypes.find(type => type.alias === contactField.type);
        if (contactField.options) {
          this.initialOptionsList = [...contactField.options];
          this.options = [...contactField.options];
        }
        this.contactFieldId = contactField.id;
      }
      this.$refs.contactFieldModal.show();
    },
    hide() {
      this.$refs.contactFieldModal.hide();
    },
    focus() {
      if (this.$refs.nameInputRef) {
        this.$refs.nameInputRef.focus();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.option {
  position: relative;

  .option-item {
    margin-bottom: 10px;
  }

  .delete {
    display: none;
    position: absolute;
    top: 5px !important;
    right: 5px !important;
    margin-right: 0 !important;
    cursor: pointer;
  }

  .error {
    margin-top: -5px;
    margin-bottom: 10px;
  }

  &:hover {
    .delete {
      display: flex;
    }
  }
}

// Make a component out of this
::v-deep .add-option-btn {
  width: max-content;
  color: $bluePrimary;

  span {
    font-weight: 500;
    font-size: 14px;
  }

  &:hover {
    cursor: pointer;
    color: $blackPrimary;
    .sb-icon .mask {
      background-color: $blackPrimary !important;
    }
  }
}
</style>
